import { useRef, useState } from "react";
import { Box, Checkbox, Typography } from "@mui/material";
import ExtractionColumnReview from "../../components/extractions/ExtractionColumnReview";
import { CustomSingleSelect } from "../../components/common/inputFields/customSingleSelect";
import { CustomTextInput } from "../../components/common/inputFields/customTextInput";
import { renderSubTitle } from "../../components/renderComponents";
import { TENANT_FIELD_CONFIG } from "../../constants/tenantFields";
import { getExtractionTenantDataPreview } from "../../services/extractionApi";
import type { ExtractionResult } from "../../types/extraction";
import type { PartialDeal } from "../../types/deal";
import type { NdaRestrictionType } from "../../types/nda";
import type { ExtractionNdaDraft } from "../../utils/extractionNda";
import {
  DEFAULT_ROW_RANGE,
  getColumnLetterForField,
  parseExcelRange,
  tenantsFromExtraction,
} from "./applyExtractionToCreateForm";

const NDA_TYPE_OPTIONS = [
  { id: "non_circumvent", name: "Non-circumvent" },
  { id: "no_contact", name: "No contact" },
];

type DealExtractionReviewProps = {
  section: "nda" | "columns";
  extraction: ExtractionResult;
  deal: PartialDeal;
  assets: any[];
  columnOverrides: Record<string, string>;
  rowRange: { start: number; end: number };
  onColumnOverridesChange: (next: Record<string, string>) => void;
  onRowRangeChange: (field: "start" | "end", value: number) => void;
  onExtractionChange: (next: ExtractionResult) => void;
  onTenantsChange: (tenants: any[]) => void;
  ndaDraft: ExtractionNdaDraft | null;
  acceptNda: boolean;
  onAcceptNdaChange: (value: boolean) => void;
  onNdaDraftChange: (next: ExtractionNdaDraft) => void;
  onError: (message: string, title: string) => void;
};

const DealExtractionReview = ({
  section,
  extraction,
  deal,
  assets,
  columnOverrides,
  rowRange,
  onColumnOverridesChange,
  onRowRangeChange,
  onExtractionChange,
  onTenantsChange,
  ndaDraft,
  acceptNda,
  onAcceptNdaChange,
  onNdaDraftChange,
  onError,
}: DealExtractionReviewProps) => {
  const [isApplyingOverrides, setIsApplyingOverrides] = useState(false);
  const latestRequestId = useRef(0);
  const latestOverrides = useRef(columnOverrides);
  latestOverrides.current = columnOverrides;

  const handleColumnChange = (field: string, value: string) => {
    const letter = value ? value.replace(/[^A-Za-z]/g, "").toUpperCase() : "";
    const next = { ...columnOverrides, [field]: letter };
    latestOverrides.current = next;
    onColumnOverridesChange(next);
  };

  const handleRecalculate = async () => {
    const start = rowRange?.start ?? DEFAULT_ROW_RANGE.start;
    const end = rowRange?.end ?? DEFAULT_ROW_RANGE.end;
    if (!start || !end || start >= end) {
      onError(
        "Please provide a valid row start and end (start must be less than end).",
        "Invalid Row Range",
      );
      return;
    }

    const requestId = Date.now();
    latestRequestId.current = requestId;
    setIsApplyingOverrides(true);

    try {
      const overrides = latestOverrides.current;
      const defaultSheetName =
        extraction.tenantColumnRanges?.find((range) => !!range.sheet_name)
          ?.sheet_name || "";

      const payloadRanges = TENANT_FIELD_CONFIG.map(({ field }) => {
        const fieldName = field as string;
        const baseRange = extraction.tenantColumnRanges?.find(
          (range) => range.field_name === fieldName,
        );
        const columnLetter = getColumnLetterForField(
          fieldName,
          overrides,
          extraction.tenantColumnRanges,
        );
        const cellRange = columnLetter
          ? `${columnLetter}${start}:${columnLetter}${end}`
          : "";
        const headerCell =
          baseRange?.header_cell ||
          (columnLetter ? `${columnLetter}${Math.max(start - 1, 1)}` : "");

        return {
          field_name: fieldName,
          sheet_name: baseRange?.sheet_name || defaultSheetName,
          cell_range: cellRange,
          header_cell: headerCell,
        };
      });

      const response = await getExtractionTenantDataPreview(extraction.id, {
        tenant_column_ranges: payloadRanges,
        attachment_index: extraction.attachmentIndex ?? 0,
        asset_names: extraction.assetNames,
      });

      if (latestRequestId.current !== requestId) return;

      if (!response.success || !response.data) {
        const raw = response.message;
        const message =
          typeof raw === "string"
            ? raw || "Failed to refresh tenant data preview."
            : raw && typeof raw === "object" && "message" in raw
              ? String((raw as { message?: unknown }).message || "")
              : "Failed to refresh tenant data preview.";
        onError(message || "Failed to refresh tenant data preview.", "Preview Error");
        return;
      }

      const incomingTenantData = response.data.tenant_dataframe;
      const hasIncomingTenantData =
        Array.isArray(incomingTenantData) && incomingTenantData.length > 0;

      const nextExtraction: ExtractionResult = {
        ...extraction,
        tenantColumnRanges:
          response.data.tenant_column_ranges &&
          response.data.tenant_column_ranges.length > 0
            ? response.data.tenant_column_ranges
            : extraction.tenantColumnRanges,
        tenantData: hasIncomingTenantData
          ? incomingTenantData
          : extraction.tenantData,
        matchedAssets:
          response.data.matched_assets !== undefined
            ? (response.data.matched_assets as Record<string, unknown> | null)
            : extraction.matchedAssets,
      };

      onExtractionChange(nextExtraction);
      onTenantsChange(
        tenantsFromExtraction(nextExtraction, assets, deal),
      );

      if (response.data.tenant_column_ranges?.length) {
        const synced = { ...overrides };
        response.data.tenant_column_ranges.forEach((range) => {
          const parsed = parseExcelRange(range.cell_range);
          if (!parsed) return;
          if (range.field_name === "asset_id") {
            synced.asset_name = parsed.startColumn;
          } else {
            synced[range.field_name] = parsed.startColumn;
          }
        });
        latestOverrides.current = synced;
        onColumnOverridesChange(synced);
      }
    } catch (error) {
      if (latestRequestId.current === requestId) {
        onError(
          error instanceof Error
            ? error.message
            : "Failed to refresh tenant data preview.",
          "Preview Error",
        );
      }
    } finally {
      if (latestRequestId.current === requestId) {
        setIsApplyingOverrides(false);
      }
    }
  };

  return (
    <>
      {section === "nda" && ndaDraft && (
        <Box sx={{ mt: 3, maxWidth: 640 }}>
          <Box sx={{ paddingBottom: 1 }}>{renderSubTitle("NDA")}</Box>
          <Typography variant="body2" sx={{ color: "#6B7280", mb: 1.5 }}>
            From extraction — will be created and linked after the deal is
            saved.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Checkbox
              disableRipple
              checked={acceptNda}
              onChange={(e) => onAcceptNdaChange(e.target.checked)}
              sx={{ color: "#1B7A63 !important", p: 0 }}
            />
            <Typography sx={{ lineHeight: 1.2 }}>
              Has restriction (create NDA)
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              opacity: acceptNda ? 1 : 0.5,
              pointerEvents: acceptNda ? "auto" : "none",
            }}
          >
            <CustomSingleSelect
              value={ndaDraft.restriction_type}
              onChange={(e) => {
                const raw = String(e.target.value || "");
                const next: NdaRestrictionType =
                  raw === "no_contact" ? "no_contact" : "non_circumvent";
                onNdaDraftChange({ ...ndaDraft, restriction_type: next });
              }}
              options={NDA_TYPE_OPTIONS}
              labelKey="name"
              placeholder="Restriction type"
              fullWidth
            />
            <CustomTextInput
              label="Expiry date"
              type="date"
              value={ndaDraft.expiry_date}
              onChange={(v) =>
                onNdaDraftChange({ ...ndaDraft, expiry_date: String(v) })
              }
              fullWidth
            />
          </Box>
        </Box>
      )}

      {section === "columns" && (
        <Box sx={{ mt: 3 }}>
          <ExtractionColumnReview
          tenantColumnRanges={extraction.tenantColumnRanges}
          columnOverrides={columnOverrides}
          onColumnChange={handleColumnChange}
          tenantData={extraction.tenantData}
          onRecalculate={handleRecalculate}
          rowRange={rowRange}
          onRowRangeChange={onRowRangeChange}
            isApplyingOverrides={isApplyingOverrides}
          />
        </Box>
      )}
    </>
  );
};

export default DealExtractionReview;
